import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getHousienUrl } from "@shared/utils/housienUrl";

/** Props for the Housien brand logo with theme-aware icon and text. */
interface HousienLogoProps {
  className?: string;
  style?: React.CSSProperties;
  size?: "xs" | "sm" | "md" | "lg";
}

const DATA_THEME_ATTRIBUTE = "data-mantine-color-scheme";

const getInitialIsDark = (): boolean => {
  const docScheme = document.documentElement.getAttribute(DATA_THEME_ATTRIBUTE);
  if (docScheme === "dark") return true;
  if (docScheme === "light") return false;

  try {
    const stored = localStorage.getItem("theme");
    return stored === "dark";
  } catch {
    return false;
  }
};

const SIZES = {
  xs: { imgHeight: "32px", imgWidth: "32px", fontSize: "1rem", marginLeft: "-8px", marginRight: "-6px", marginTop: "0px", paddingBottom: "4px" },
  sm: { imgHeight: "64px", imgWidth: "64px", fontSize: "1.5rem", marginLeft: "-16px", marginRight: "-12px", marginTop: "30px", paddingBottom: "16px" },
  md: { imgHeight: "56px", imgWidth: "56px", fontSize: "1.4rem", marginLeft: "-14px", marginRight: "-10px", marginTop: "4px", paddingBottom: "14px" },
  lg: { imgHeight: "64px", imgWidth: "64px", fontSize: "1.5rem", marginLeft: "-16px", marginRight: "-12px", marginTop: "5px", paddingBottom: "16px" },
} as const;

function resolveLogoSource(isDark: boolean): string {
  return isDark ? "/images/logo/housien-dark.png" : "/images/logo/housien-light.png";
}

function resolveTextColor(isDark: boolean): string {
  return isDark ? "#F1E4CA" : "#621217";
}

const HousienLogo: React.FC<HousienLogoProps> = ({ className, style, size = "md" }) => {
  const { t } = useTranslation();
  const [isDark, setIsDark] = useState(getInitialIsDark);

  useEffect(() => {
    const checkTheme = () => {
      const scheme = document.documentElement.getAttribute(DATA_THEME_ATTRIBUTE);
      setIsDark(scheme === "dark");
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [DATA_THEME_ATTRIBUTE],
    });

    return () => observer.disconnect();
  }, []);

  const logoSource = resolveLogoSource(isDark);

  const s = SIZES[size];

  const housienUrl = getHousienUrl();

  return (
    <a
      href={housienUrl}
      className={`inline-flex items-end shrink-0 select-none no-underline text-inherit ${className || ""}`}
      style={{ gap: 0, ...style }}
      aria-label={t("help.housienLogoAlt")}
    >
      <img
        key={`logo-${isDark ? "dark" : "light"}`}
        src={logoSource}
        alt=""
        aria-hidden="true"
        className="block object-contain"
        style={{
          height: s.imgHeight,
          width: s.imgWidth,
          marginLeft: s.marginLeft,
          marginRight: s.marginRight,
          marginTop: s.marginTop,
          userSelect: "none",
          WebkitUserDrag: "none",
        } as React.CSSProperties}
      />
      <span
        className="whitespace-nowrap shrink-0 leading-none"
        style={{
          fontFamily: "var(--font-family)",
          fontSize: s.fontSize,
          fontWeight: 350,
          color: resolveTextColor(isDark),
          letterSpacing: "-0.01em",
          paddingBottom: s.paddingBottom,
          textDecoration: "none",
        }}
      >
        ousien
      </span>
    </a>
  );
};

export default HousienLogo;
